import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import klubService from '../../services/klubService';
import { notify } from '../../components/common/AppToast';
import { ROUTES } from '../../config/routes';
import useAppConfirm from '../../hooks/useAppConfirm';
import { AppButton, AppFormField, AppInput, AppSpinner } from '../../components/ui';
import KlubDashboardStatCard from '../../components/klub/KlubDashboardStatCard';
import KlubSiteList from '../../components/klub/KlubSiteList';
import {
  LIFECYCLE_PHASES,
  PHASE_ACTIFS,
  PHASE_INTEGRATION,
  PHASE_RESILIATION,
  PREAVIS_BUCKETS,
} from '../../utils/klubPipelinePhases';
import '../../styles/klub-dashboard.scss';

const PHASE_CARDS = [
  {
    code: PHASE_INTEGRATION,
    label: "En cours d'intégration",
    hint: 'Sites pas encore en ligne',
    icon: 'mdi:progress-clock',
    color: 'warning',
  },
  {
    code: PHASE_ACTIFS,
    label: 'Actifs',
    hint: 'Sites en ligne',
    icon: 'mdi:check-circle-outline',
    color: 'success',
  },
  {
    code: PHASE_RESILIATION,
    label: 'Résiliation',
    hint: 'Demandes et sites résiliés',
    icon: 'mdi:cancel',
    color: 'danger',
  },
];

const dashboardUrl = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  const qs = query.toString();
  return qs ? `${ROUTES.klub.dashboard}?${qs}` : ROUTES.klub.dashboard;
};

const KlubDashboardPage = () => {
  const { ask, modal } = useAppConfirm();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [realigning, setRealigning] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [adherents, setAdherents] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [catalogReady, setCatalogReady] = useState(false);
  const vue = searchParams.get('vue') || '';
  const etape = searchParams.get('etape') || '';
  const [scrolled, setScrolled] = useState(false);

  const loadStats = ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    return klubService.getStats()
      .then(setStats)
      .catch((err) => notify.error(err.message))
      .finally(() => setLoading(false));
  };

  const loadList = ({ silent = false } = {}) => {
    if (!silent) setListLoading(true);
    return klubService.listAdherents({
      search,
      lifecycle_phase: vue,
      current_workflow_step_code: vue && vue !== PHASE_ACTIFS ? etape : '',
      preavis: vue === PHASE_ACTIFS ? etape : '',
      limit: 200,
    })
      .then((res) => setAdherents(res.adherents ?? []))
      .catch((err) => notify.error(err.message))
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = window.scrollY > 16;
        setScrolled((prev) => (prev === next ? prev : next));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    klubService.getPipelineTemplate()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) setCatalogReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!catalogReady) return undefined;
    loadStats();
    return undefined;
  }, [catalogReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!catalogReady) return undefined;
    loadList();
    return undefined;
  }, [catalogReady, search, vue, etape]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async () => {
    const ok = await ask({
      title: 'Importer les données Excel ?',
      message: 'Les données existantes seront conservées.',
      confirmLabel: 'Importer',
      confirmVariant: 'primary',
    });
    if (!ok) {
      return;
    }
    setImporting(true);
    try {
      const result = await klubService.importExcel();
      notify.success(
        `Import : ${result.adherents} adhérents, ${result.ce} CE, ${result.avis} avis (${result.matches} matchs Dolibarr auto)`,
      );
      await Promise.all([loadStats(), loadList({ silent: true })]);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleRealign = async () => {
    const ok = await ask({
      title: 'Réaligner les coordonnées ?',
      message: 'Met à jour le contact, les téléphones, l’e-mail et l’adresse des fiches déjà présentes, à partir du fichier Excel. Aucune fiche n’est créée.',
      confirmLabel: 'Réaligner',
      confirmVariant: 'primary',
    });
    if (!ok) {
      return;
    }
    setRealigning(true);
    try {
      const result = await klubService.realignExcel();
      notify.success(
        `Réalignement : ${result.adherents} adhérents, ${result.ce} CE (${result.missing_adherents} adhérents et ${result.missing_ce} CE absents du fichier)`,
      );
      await Promise.all([loadStats(), loadList({ silent: true })]);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setRealigning(false);
    }
  };

  const phaseCounts = useMemo(
    () => Object.fromEntries((stats?.par_phase ?? []).map((item) => [item.code, item.count ?? 0])),
    [stats],
  );

  const stepsByPhase = useMemo(() => {
    const buckets = Object.fromEntries(LIFECYCLE_PHASES.map((phase) => [phase.code, []]));
    (stats?.par_etape ?? []).forEach((step) => {
      if (step.checklist === false) return;
      if (!buckets[step.phase]) buckets[step.phase] = [];
      buckets[step.phase].push(step);
    });
    Object.values(buckets).forEach((list) => list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    return buckets;
  }, [stats]);

  const preavisCards = useMemo(() => {
    const counts = Object.fromEntries((stats?.par_preavis ?? []).map((item) => [item.code, item]));
    return PREAVIS_BUCKETS.map((bucket) => ({
      ...bucket,
      count: counts[bucket.code]?.count ?? 0,
      hint: counts[bucket.code]?.hint ?? bucket.hint,
    }));
  }, [stats]);

  const subCards = useMemo(() => {
    if (vue === PHASE_INTEGRATION) {
      return (stepsByPhase[PHASE_INTEGRATION] ?? []).map((step) => ({
        key: step.step_code,
        label: step.label,
        hint: step.card_hint || '',
        value: step.count ?? 0,
        color: 'warning',
        icon: 'mdi:format-list-checks',
      }));
    }
    if (vue === PHASE_ACTIFS) {
      const fills = {
        urgent: 'rose',
        imminent: 'orange',
        ras: 'green',
        depasse: 'white',
      };
      const icons = {
        depasse: 'mdi:do-not-enter',
        urgent: 'mdi:alert-outline',
        imminent: 'mdi:clock-alert-outline',
        ras: 'mdi:check-decagram-outline',
      };
      return preavisCards.map((bucket) => ({
        key: bucket.code,
        label: bucket.label,
        hint: bucket.hint,
        value: bucket.count,
        fill: fills[bucket.code] ?? 'white',
        color: 'secondary',
        icon: icons[bucket.code] ?? 'mdi:calendar-outline',
      }));
    }
    if (vue === PHASE_RESILIATION) {
      return (stepsByPhase[PHASE_RESILIATION] ?? []).map((step) => ({
        key: step.step_code,
        label: step.label,
        hint: step.card_hint || '',
        value: step.count ?? 0,
        color: 'danger',
        icon: 'mdi:file-cancel-outline',
      }));
    }
    return [];
  }, [vue, stepsByPhase, preavisCards]);

  return (
    <MasterLayout>
      <Breadcrumb title="Klub — Dashboard" hideHeading />

      <div className="d-flex justify-content-between align-items-center mb-24 flex-wrap gap-3">
        <div>
          <h4 className="fw-semibold mb-1">Sites Klub</h4>
          <p className="text-secondary-light mb-0">Suivi des sites par grande étape</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <AppButton as={Link} to={ROUTES.klub.adherentNew} size="sm" variant="primary" icon="mdi:plus">
            Nouvel site
          </AppButton>
          <AppButton
            variant="outline-primary"
            size="sm"
            icon="mdi:file-excel"
            onClick={handleImport}
            loading={importing}
            loadingLabel="Import…"
            disabled={realigning}
          >
            Importer Excel
          </AppButton>
          <AppButton
            variant="outline-secondary"
            size="sm"
            icon="mdi:sync"
            onClick={handleRealign}
            loading={realigning}
            loadingLabel="Réalignement…"
            disabled={importing}
          >
            Réaligner
          </AppButton>
        </div>
      </div>

      {loading ? (
        <AppSpinner />
      ) : (
        <div className="klub-dashboard">
          <div className={`klub-dashboard__pin${scrolled ? ' is-scrolled' : ''}`}>
            <div className="klub-dashboard__stats klub-dashboard__stats--phases">
              {PHASE_CARDS.map((card) => (
                <KlubDashboardStatCard
                  key={card.code}
                  to={dashboardUrl(vue === card.code ? {} : { vue: card.code })}
                  active={vue === card.code}
                  color={card.color}
                  icon={card.icon}
                  label={card.label}
                  hint={card.hint}
                  value={phaseCounts[card.code] ?? 0}
                />
              ))}
            </div>

            {subCards.length > 0 && (
              <div className="klub-dashboard__stats klub-dashboard__stats--sub klub-dashboard__stats--compact">
                {subCards.map((card) => (
                  <KlubDashboardStatCard
                    key={card.key}
                    to={dashboardUrl({
                      vue,
                      etape: etape === card.key ? '' : card.key,
                    })}
                    active={etape === card.key}
                    color={card.color}
                    fill={card.fill}
                    icon={card.icon}
                    label={card.label}
                    hint={card.hint}
                    value={card.value}
                  />
                ))}
              </div>
            )}

            <AppFormField label="Recherche client" htmlFor="klub-dashboard-search" className="mb-0">
              <AppInput
                id="klub-dashboard-search"
                value={searchInput}
                placeholder="Entreprise, contact, email…"
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </AppFormField>
          </div>

          <KlubSiteList
            adherents={adherents}
            loading={listLoading}
            stepsByPhase={stepsByPhase}
            phase={vue}
            listReturnTo={dashboardUrl({ vue, etape })}
            onChanged={() => Promise.all([loadStats({ silent: true }), loadList({ silent: true })])}
          />
        </div>
      )}
      {modal}
    </MasterLayout>
  );
};

export default KlubDashboardPage;
